import constructFabricUrl from 'commons/constructFabricUrl';
import { camelCase } from 'lodash';
import { PANTONE_FABRIC_CONFIG } from 'commons/constants';

const { fabricMappingOverrides } = PANTONE_FABRIC_CONFIG;

export const getUniqueArray = allItems => [...new Set(allItems)];

export const mobileItemsPerRowOptions = [1, 2, 3];

const fabricNameToCamelCaseOverrides = fabricMappingOverrides.reduce(
  (acc, { camelCaseId, name }) => ({
    ...acc,
    [name]: camelCaseId,
  }),
  {}
);

const camelCaseToFabricNameOverrides = fabricMappingOverrides.reduce(
  (acc, { camelCaseId, name }) => ({
    ...acc,
    [camelCaseId]: name,
  }),
  {}
);

export const getFabricNameFromCamelCaseSlug = camelCaseSlug => {
  if (camelCaseToFabricNameOverrides[camelCaseSlug]) {
    return camelCaseToFabricNameOverrides[camelCaseSlug];
  }

  const spacedText = camelCaseSlug
    .replace(/([A-Z])/g, ' $1')
    .replace(/( Slsh )/g, '/');
  const capitalized = spacedText.charAt(0).toUpperCase() + spacedText.slice(1);

  return capitalized;
};

export const getCamelCaseSlugFromFabricName = fabricName => {
  if (fabricNameToCamelCaseOverrides[fabricName]) {
    return fabricNameToCamelCaseOverrides[fabricName];
  }

  const camelCaseSlug = camelCase(
    fabricName.replace(/[/]/g, ' Slsh').replace(/[_-\s]/g, ' ')
  );

  return camelCaseSlug;
};

export const normalizeString = value => getFabricNameFromCamelCaseSlug(value);

export const normalizeArray = textArrayRaw => {
  if (!textArrayRaw) return {};
  const valueArray = textArrayRaw
    .split(',')
    .map(value => getFabricNameFromCamelCaseSlug(value));
  return Object.fromEntries(valueArray.map(val => [val, true]));
};

export const normalizeArrayUnderscored = textArrayRaw => {
  if (!textArrayRaw) return {};
  const normalizeUnderscoredValue = text => {
    const spacedText = text
      .replace(/([A-Z])/g, '_$1')
      .replace(/( Slsh )/g, '/');
    const capitalized =
      spacedText.charAt(0).toUpperCase() + spacedText.slice(1);
    return capitalized.toLowerCase();
  };
  const valueArray = textArrayRaw
    .split(',')
    .map(value => normalizeUnderscoredValue(value));
  return Object.fromEntries(valueArray.map(val => [val, true]));
};

export const normalizeNumberArray = textArrayRaw => {
  if (!textArrayRaw) return [];
  return textArrayRaw.split(',').map(value => parseInt(value, 10));
};

export const getNormalizedFabricQueryValue = (key, value) => {
  if (['material', 'sort', 'fabric', 'leather'].includes(key)) {
    return normalizeString(value);
  }

  if (key === 'color') {
    return normalizeArray(value);
  }

  if (key === 'feature') {
    return normalizeArrayUnderscored(value);
  }

  if (key === 'tier') {
    return normalizeNumberArray(value);
  }

  return value;
};

export const sortByOptions = [
  { value: '', label: 'Popular' },
  { value: 'Colors', label: 'Color' },
  { value: 'Family Name', label: 'Family' },
];

const getOptionURL = (fabricName, optionValue) => {
  const option_url = optionValue
    .trim()
    .toLowerCase()
    .replace(/ /g, '-');

  return fabricName === 'Wood Stain' ? '#' : `swatches/swatch/${option_url}`;
};

const parseGroupMaterial = groupMaterial => {
  try {
    const parsed = JSON.parse(groupMaterial);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [groupMaterial];
  }
};

export const fabricObject = fabric => ({
  option: {
    type: fabric.option_name,
    group: fabric.group_name,
    color: fabric.option_color,
    pet_friendly: !!fabric.pet_friendly,
    sustainable_fabric: fabric.sustainable_fabric,
    material: fabric.group_material_desc || '',
    cleaning_code: fabric.group_cleaning_code || '',
    tier: fabric.option_value_tier || '',
    id: fabric.option_value_id,
    groupMaterial: parseGroupMaterial(fabric.group_material),
    description: (fabric.option_description || '').replace(/(<([^>]+)>)/gi, ''),
  },
  linkButton: {
    link: getOptionURL(fabric.option_name, fabric.option_value),
    content: fabric.option_value,
  },
  alignment: 'middle',
  subTitle: {
    name:
      fabric.option_description !== null
        ? fabric.option_description.replace(/(<([^>]+)>)/gi, '')
        : '',
    style: {
      color: '#FFFFFF',
      maxWidth: '231px',
      fontSize: '12px',
    },
  },
  imageProps: {
    src:
      fabric.option_name === 'Fabric'
        ? constructFabricUrl(
            fabric.option_value,
            null,
            'shop-color',
            fabric.option_value_id
          ).transparent_config_image
        : `https://joybird2.imgix.net/option_images/swatches/290x290-${fabric.option_image}`,
    fallbackSrc: `https://joybird2.imgix.net/option_images/swatches/290x290-${fabric.option_image}`,
    height: 300,
    width: 650,
  },
  footerTitle: {
    style: {
      fontSize: '12px',
      color: '#585858',
    },
    name: fabric.option_value,
  },
});
